import { idToken } from "./sitzung";

/**
 * Befehls-API (docs/contracts/befehle.md) und Zeremonien (identitaeten.md). Die Antwort ist für die App, nicht für die
 * Suite: Sie liest daraus Kennungen, Zusatzfelder (z. B. den Einladungslink) und bei Abweisung den Grund.
 */
export interface ApiAntwort {
  ok: boolean;
  status: number;
  grund: string | null;
  kennungen: Record<string, string>;
  felder: Record<string, unknown>;
  serverzeit: string | null;
  /** kein Netz bzw. keine Antwort */
  netzfehler: boolean;
}

function auswerten(status: number, k: Record<string, unknown>): ApiAntwort {
  const { ergebnis, grund, kennungen, serverzeit, ...felder } = k;
  return {
    ok: status === 200 && ergebnis === "angenommen", status, grund: typeof grund === "string" ? grund : null,
    kennungen: (kennungen as Record<string, string> | undefined) ?? {}, felder, serverzeit: typeof serverzeit === "string" ? serverzeit : null,
    netzfehler: false,
  };
}

const NETZFEHLER: ApiAntwort = { ok: false, status: 0, grund: null, kennungen: {}, felder: {}, serverzeit: null, netzfehler: true };

/**
 * Anzeigewert für „keine verwertbare Antwort des Dienstes“: kein Netz, abgebrochene Verbindung, keine Sitzung zu holen
 * oder eine Antwort ohne Grund (z. B. eine Fehlerseite vor dem Dienst). Kein Abweisungsgrund aus befehle.md §7, sondern
 * der Anlass für die feste Meldung „Keine Verbindung. …“ (oberflaeche.md §9, `design/Hinweis.tsx`).
 */
export const OHNE_ANTWORT = "ohne-antwort";

/**
 * Was ein Dialog nach einem Befehl als Abweisung zeigt (Anforderungen K106 (2): keine Abweisung bleibt stumm): `null`,
 * wenn der Befehl angenommen ist, sonst der Grund des Dienstes oder {@link OHNE_ANTWORT}.
 */
export function abweisungsgrund(a: ApiAntwort): string | null {
  if (a.ok) return null;
  return a.grund ?? OHNE_ANTWORT;
}

/**
 * Grund, mit dem die alte Adresse im Umzugsmodus jeden Schreibversuch beantwortet (HTTP 503, identitaeten.md §11). Eine
 * App, die das noch nicht weiß (vor dem Umzug geöffnet, ohne Netz gestartet), startet einmal neu: Der Start lädt
 * `/config.json` und zeigt den Hinweis mit „Umziehen“ bzw. „Öffnen“ (oberflaeche.md V-28). Eine Meldung der
 * Warteschlange bleibt dabei wartend (5xx) und geht nicht als abgewiesen verloren.
 */
export const UMGEZOGEN = "umgezogen";
let umzugGefolgt = false;

function umzugFolgen(): void {
  if (umzugGefolgt) return;
  umzugGefolgt = true;
  window.location.replace("/");
}

/** Bricht eine Anfrage ab, ohne dass eine Antwort kam (Netzwechsel), wird eine wiederholbare einmal neu gesendet. */
const WIEDERHOLEN_NACH_MS = 300;

async function senden(pfad: string, body: unknown, mitSitzung: boolean, wiederholbar: boolean): Promise<ApiAntwort> {
  const kopf: Record<string, string> = { "Content-Type": "application/json; charset=utf-8" };
  if (mitSitzung) {
    // Ein Token, das ohne Netz nicht erneuert werden kann, ist eine fehlende Antwort, kein Absturz des Handlers.
    const t = await idToken().catch(() => undefined);
    if (t === undefined) return NETZFEHLER;
    if (t) kopf["Authorization"] = `Bearer ${t}`;
  }
  for (let versuch = 1; ; versuch++) {
    try {
      const r = await fetch(pfad, { method: "POST", headers: kopf, body: JSON.stringify(body) });
      const k = (await r.json().catch(() => ({}))) as Record<string, unknown>;
      const a = auswerten(r.status, k);
      if (a.grund === UMGEZOGEN) umzugFolgen();
      return a;
    } catch {
      if (!wiederholbar || versuch >= 2 || !navigator.onLine) return NETZFEHLER;
      await new Promise((weiter) => setTimeout(weiter, WIEDERHOLEN_NACH_MS));
    }
  }
}

/**
 * Zeremonie unter `/api/zugang/…`; mit Sitzung nur, wo identitaeten.md es verlangt. Optionen anfordern ist ohne
 * Wirkung und darf nach einem Verbindungsabbruch wiederholt werden; ein Abschluss nicht (die Antwort ist verbraucht).
 */
export function zugang(pfad: string, body: Record<string, unknown>, mitSitzung = false): Promise<ApiAntwort> {
  return senden(`/api/zugang/${pfad}`, body, mitSitzung, pfad.endsWith("/optionen"));
}

export interface Umschlag {
  meldungsId: string;
  geraetezeit: string;
  daten: Record<string, unknown>;
}

/** Neuer Umschlag: Meldungs-Id durch genau einen Aufruf von `crypto.randomUUID()` beim Erfassen (teststeuerung.md §10). */
export function umschlag(daten: Record<string, unknown>): Umschlag {
  return { meldungsId: crypto.randomUUID(), geraetezeit: new Date().toISOString(), daten };
}

/**
 * Befehl über die API. Jeder Umschlag trägt seine Meldungs-Id, die Befehls-API macht eine Wiederholung wirkungslos
 * (befehle.md §5: dieselbe Antwort, `wiederholt: true`). Bricht die Verbindung ab, ohne dass eine Antwort kam (Netzwechsel,
 * eine vom Server geschlossene Verbindung), sendet die App denselben Umschlag deshalb gleich noch einmal, statt einen
 * Netzfehler zu melden. Felder, die nie gespeichert werden (z. B. ein Einladungslink), fehlen in einer wiederholten
 * Antwort.
 */
export function befehlSenden(name: string, u: Umschlag): Promise<ApiAntwort> {
  return senden(`/api/befehle/${name}`, u, true, true);
}

/** Formularbefehl mit Netz (Planung, Verwaltung). Meldungen im Feld gehen über die Warteschlange. */
export function befehl(name: string, daten: Record<string, unknown>): Promise<ApiAntwort> {
  return befehlSenden(name, umschlag(daten));
}
